import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { QrPanel } from '@/components/shared/qr-panel';
import { Tip } from '@/components/shared/tip';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/toast';
import { useTotpSetupFlow } from '@/hooks/use-totp-setup-flow';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { useAuthStore } from '@/stores/auth-store';

export const TotpSection = () => {
  const { t } = useTranslation('security');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const setUser = useAuthStore((state) => state.setUser);
  const user = useAuthStore((state) => state.user);

  const [disableOpen, setDisableOpen] = useState(false);
  const [disableCode, setDisableCode] = useState('');

  const flow = useTotpSetupFlow();

  const status = useQuery({
    queryKey: ['totp', 'status'],
    queryFn: () =>
      api.get<{ enabled: boolean; recoveryCodesRemaining: number }>('/auth/totp', {
        withoutChurch: true,
      }),
  });

  const disable = useMutation({
    mutationFn: () =>
      api.post<{ disabled: true }>('/auth/totp/disable', { code: disableCode }, { withoutChurch: true }),
    onSuccess: async () => {
      setDisableCode('');
      setDisableOpen(false);
      flow.reset();
      toast.success(t('totp.disabledToast'));
      void queryClient.invalidateQueries({ queryKey: ['totp', 'status'] });
      const me = await api.get<NonNullable<typeof user>>('/auth/me', { withoutChurch: true });
      setUser(me);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const enabled = status.data?.enabled ?? user?.isTotpEnabled ?? false;

  return (
    <Card id="account-totp" className="h-full scroll-mt-6">
      <CardHeader>
        <CardTitle>{t('totp.title')}</CardTitle>
        <CardDescription>{t('totp.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-body-sm">{enabled ? t('totp.enabled') : t('totp.disabled')}</p>
        {enabled && status.data ? (
          <p className="text-body-sm text-fg-secondary">
            {t('totp.recoveryRemaining', { count: status.data.recoveryCodesRemaining })}
          </p>
        ) : null}

        {flow.setupUrl ? (
          <div className="space-y-3">
            <p className="text-body-sm">{t('totp.scan')}</p>
            <QrPanel value={flow.setupUrl} label={t('totp.scan')} size={180} />
          </div>
        ) : null}

        {flow.recoveryCodes ? (
          <div className="space-y-2 rounded-lg border border-border p-3">
            <p className="font-medium">{t('totp.recoveryTitle')}</p>
            <p className="text-body-sm text-fg-secondary">{t('totp.recoveryDescription')}</p>
            <ul className="grid grid-cols-2 gap-1 font-mono text-body-sm">
              {flow.recoveryCodes.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={flow.acknowledgeRecoveryCodes}>
                {t('totp.copyCodes')}
              </Button>
            </div>
          </div>
        ) : null}

        {(flow.setupUrl || enabled) && !flow.recoveryCodes ? (
          <div className="flex max-w-xs flex-col gap-1.5">
            <Label htmlFor="totp-code">{t('totp.code')}</Label>
            <Input
              id="totp-code"
              value={flow.setupUrl ? flow.code : disableCode}
              onChange={(event) =>
                flow.setupUrl
                  ? flow.setCode(event.target.value)
                  : setDisableCode(event.target.value)
              }
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
            />
            <Tip tipId="security.totpCode" />
          </div>
        ) : null}
      </CardContent>
      <CardFooter className="flex justify-end gap-2">
        {!enabled && !flow.setupUrl ? (
          <Button loading={flow.isStarting} onClick={() => flow.startSetup()}>
            {t('totp.setup')}
          </Button>
        ) : null}
        {flow.setupUrl ? (
          <Button
            loading={flow.isConfirming}
            disabled={flow.code.length < 6}
            onClick={() => flow.confirmSetup()}
          >
            {t('totp.confirm')}
          </Button>
        ) : null}
        {enabled && !flow.setupUrl ? (
          <Button
            variant="destructive"
            disabled={disableCode.length < 6}
            onClick={() => setDisableOpen(true)}
          >
            {t('totp.disable')}
          </Button>
        ) : null}
      </CardFooter>

      <ConfirmDialog
        open={disableOpen}
        onOpenChange={setDisableOpen}
        title={t('confirm.disableTotp.title')}
        description={t('confirm.disableTotp.description')}
        confirmLabel={t('confirm.disableTotp.confirm')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={disable.isPending}
        onConfirm={() => disable.mutate()}
      />
    </Card>
  );
};
